import { modalAnatomy as parts } from '@chakra-ui/anatomy';
import { createMultiStyleConfigHelpers, defineStyle } from '@chakra-ui/react';
import type {
    ModalProps,
    PartsStyleInterpolation,
    PartsStyleObject,
    SystemStyleObject,
} from '@chakra-ui/react';

// eslint-disable-next-line @typescript-eslint/unbound-method
const { defineMultiStyleConfig, definePartsStyle } = createMultiStyleConfigHelpers(parts.keys);

const sharedStyleOverlay: SystemStyleObject = defineStyle({
    background: 'slt-black-overlay',
    zIndex: 'modal',
});

const sharedStyleCloseButton: SystemStyleObject = defineStyle({
    padding: 0,
    width: 6,
    height: 6,
    top: 4,
    right: 3,
    _focus: {
        boxShadow: 'none',
    },
});

const sharedStyleHeader: SystemStyleObject = defineStyle({
    paddingTop: 0,
    paddingX: 0,
    paddingBottom: 4,
});

const sharedStyleBody: SystemStyleObject = defineStyle({
    textStyle: 'slt-body-text-lg',
    padding: 0,
});

const sharedStyleFooter: SystemStyleObject = defineStyle({
    paddingTop: 4,
    paddingX: 0,
    paddingBottom: 0,
});

const sharedStyleDialog: SystemStyleObject = defineStyle({
    padding: 4,
    maxWidth: '100%',
    maxHeight: '100%',
});

const shared: PartsStyleObject = definePartsStyle({
    overlay: sharedStyleOverlay,
    closeButton: sharedStyleCloseButton,
    header: sharedStyleHeader,
    body: sharedStyleBody,
    footer: sharedStyleFooter,
});

const overflowContent: PartsStyleObject = definePartsStyle({
    ...shared,
    dialog: {
        ...sharedStyleDialog,
        width: '60vw',
        minWidth: '640px',
        maxWidth: '80vw',
        height: '60vh',
        minHeight: '460px',
    },
    body: {
        ...sharedStyleBody,
        paddingRight: 2,
    },
});

const sltVariants: Record<string, PartsStyleInterpolation> = {
    'hug-content': {
        ...shared,
        dialog: {
            ...sharedStyleDialog,
            width: { base: '640px' },
        },
    },
    'hug-content-wide-buttons': {
        ...shared,
        dialog: {
            ...sharedStyleDialog,
            width: { base: '640px' },
        },
        footer: {
            ...sharedStyleFooter,
            Button: {
                width: '50%',
            },
        },
    },
    'hug-content-small': {
        ...shared,
        dialog: {
            ...sharedStyleDialog,
            width: { base: '464px', lg: '554px' },
        },
    },
    'overflow-content': {
        ...overflowContent,
    },
    'overflow-content-wide-button': {
        ...overflowContent,
        footer: {
            ...sharedStyleFooter,
            Button: {
                width: '50%',
            },
        },
    },
    'overflow-content-check-box': {
        ...overflowContent,
        footer: {
            ...sharedStyleFooter,
            div: {
                justifyContent: 'space-between',
            },
        },
    },
    'ui-default': definePartsStyle({
        dialog: {
            borderRadius: 0,
        },
        body: {
            paddingTop: 0,
            paddingX: 4,
            paddingBottom: 4,
        },
    }),
    'turn-to': definePartsStyle({
        dialog: {
            '-ms-overflow-style': 'none',
            'scrollbar-width': 'none',
            '::-webkit-scrollbar': {
                display: 'none',
            },
            overflowY: 'auto',
            maxHeight: '90%',
            minWidth: {
                md: '44.69rem',
            },
        },
        body: {
            padding: {
                md: 4,
            },
        },
        header: {
            padding: {
                md: 4,
            },
            paddingBottom: {
                md: 0,
            },
        },
    }),
    'no-frame': definePartsStyle({
        dialog: {
            padding: 0,
            maxWidth: { base: 'calc(100% - 68px)', lg: '90%' },
        },
        body: {
            padding: 0,
        },
    }),
    fullscreen: definePartsStyle({
        dialog: {
            borderRadius: 0,
            maxWidth: {
                base: 'min(100dvw, 100vw, 100%)',
                md: 'min(80dvw, 80vw, 80%)',
            },
            width: {
                base: 'min(100dvw, 100vw, 100%)',
                md: 'min(80dvw, 80vw, 80%)',
            },
            maxHeight: {
                base: 'min(100dvh, 100vh, 100%)',
                md: 'min(80dvh, 80vh, 80%)',
            },
            height: {
                base: 'min(100dvh, 100vh, 100%)',
                md: 'min(80dvh, 80vh, 80%)',
            },
        },
    }),
    fullscreen_legacy: definePartsStyle({
        dialog: {
            borderRadius: 0,
            maxWidth: {
                base: '100%',
                md: '80%',
            },
            width: {
                base: '100%',
                md: '80%',
            },
            maxHeight: {
                base: '100%',
                md: '80%',
            },
            height: {
                base: '100%',
                md: '80%',
            },
        },
    }),
};

/**
 * Since the `maxWidth` prop references theme.sizes internally,
 * we can leverage that to size our modals.
 */
function getSize(value: string): PartsStyleObject {
    return definePartsStyle({
        dialog: { minW: value, maxW: value, w: value },
    });
}

const sizes = {
    compact: getSize('2/5'),
    extended: getSize('11/20'),
    auto: getSize('auto'),
};

const modalTheme = defineMultiStyleConfig({
    variants: sltVariants,
    sizes,
    defaultProps: { variant: 'hug-content' },
});

type ThemeStyleConfig = ReturnType<typeof defineMultiStyleConfig>;
type ModalTheme = ThemeStyleConfig & {
    defaultProps: ThemeStyleConfig['defaultProps'] & Pick<ModalProps, 'isCentered'>;
};
export const Modal: ModalTheme = {
    ...modalTheme,
    defaultProps: { ...modalTheme.defaultProps, isCentered: true },
};
